import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

export interface User {
  id: string
  email: string
  name: string
  avatar?: string
  role: 'admin' | 'manager' | 'user'
  department?: string
  joinDate: string
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  loginAttempts: number
  lastLoginAttempt: number | null
}

export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

export interface RegisterData {
  email: string
  password: string
  name: string
  confirmPassword: string
}

export interface AuthResponse {
  user: User
  token: string
  refreshToken: string
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  loginAttempts: 0,
  lastLoginAttempt: null,
}

// Async thunks for auth operations
export const loginUser = createAsyncThunk<
  AuthResponse,
  LoginCredentials,
  { rejectValue: string }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      // Simulate API call - replace with real API
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Mock successful login
      if (credentials.email === 'demo@example.com' && credentials.password === 'password') {
        const mockResponse: AuthResponse = {
          user: {
            id: '1',
            email: credentials.email,
            name: 'Demo User',
            avatar: 'DU',
            role: 'manager',
            department: 'Engineering',
            joinDate: '2025-01-01'
          },
          token: 'mock-jwt-token',
          refreshToken: 'mock-refresh-token'
        }
        
        // Store tokens in localStorage
        localStorage.setItem('token', mockResponse.token)
        localStorage.setItem('refreshToken', mockResponse.refreshToken)
        if (credentials.rememberMe) {
          localStorage.setItem('rememberMe', 'true')
        }
        
        return mockResponse
      } else {
        throw new Error('Invalid email or password')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Login failed')
    }
  }
)

export const registerUser = createAsyncThunk<
  AuthResponse,
  RegisterData,
  { rejectValue: string }
>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1200))
      
      // Mock validation
      if (userData.password !== userData.confirmPassword) {
        throw new Error('Passwords do not match')
      }
      
      if (userData.password.length < 6) {
        throw new Error('Password must be at least 6 characters')
      }
      
      // Mock successful registration
      const mockResponse: AuthResponse = {
        user: {
          id: Date.now().toString(),
          email: userData.email,
          name: userData.name,
          avatar: userData.name.split(' ').map(n => n[0]).join('').toUpperCase(),
          role: 'user',
          joinDate: new Date().toISOString().split('T')[0]
        },
        token: 'mock-jwt-token',
        refreshToken: 'mock-refresh-token'
      }
      
      // Store tokens
      localStorage.setItem('token', mockResponse.token)
      localStorage.setItem('refreshToken', mockResponse.refreshToken)
      
      return mockResponse
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Registration failed')
    }
  }
)

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    // Clear stored tokens
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('rememberMe')
    
    // In a real app, you might want to invalidate the token on the server
    return true
  }
)

export const checkAuthStatus = createAsyncThunk<
  User,
  void,
  { rejectValue: string }
>(
  'auth/checkStatus',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token')
      
      if (!token) {
        throw new Error('No token found')
      }
      
      // Simulate API call to verify token and get user
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Mock user data - in real app, decode token or call API
      const mockUser: User = {
        id: '1',
        email: 'demo@example.com',
        name: 'Demo User',
        avatar: 'DU',
        role: 'manager',
        department: 'Engineering',
        joinDate: '2025-01-01'
      }
      
      return mockUser
    } catch (error) {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      return rejectWithValue('Authentication failed')
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0
      state.lastLoginAttempt = null
    },
  },
  extraReducers: (builder) => {
    // Login cases
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.error = null
        state.loginAttempts = 0
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Login failed'
        state.loginAttempts += 1
        state.lastLoginAttempt = Date.now()
      })
      
    // Register cases
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.error = null
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Registration failed'
      })
      
    // Logout cases
    builder
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.error = null
        state.loginAttempts = 0
        state.lastLoginAttempt = null
      })
      
    // Check auth status cases
    builder
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.user = action.payload
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.isLoading = false
        state.isAuthenticated = false
        state.user = null
      })
  },
})

export const { clearError, updateUser, resetLoginAttempts } = authSlice.actions
export default authSlice.reducer 